import React, { lazy, createElement } from 'react';
import { REDIRECT_PATHS } from './navigationConfig';

export interface RouteItem {
  title: string;
  path?: string;
  iconName?: string;
  component?: React.ComponentType | string;
  subitems?: RouteItem[];
  devOnly?: boolean;
  enabled?: boolean;
  folder?: string;
  hideFromMenu?: boolean;
  children?: RouteItem[];
  userStatus?: string[];
  isExternal?: boolean;
}

const featureModules = import.meta.glob('../features/**/*.{tsx,jsx}', { eager: false });
const componentModules = import.meta.glob('../components/**/*.{tsx,jsx}', { eager: false });

const getComponent = (componentPath: string): React.ComponentType => {
  try {
    const cleanPath = componentPath.replace(/\.(tsx?|jsx?)$/, '');

    const possiblePaths = [
      `../features/${cleanPath.replace('features/', '')}.tsx`,
      `../features/${cleanPath.replace('features/', '')}.jsx`,
      `../components/${cleanPath.replace('components/', '')}.tsx`,
      `../components/${cleanPath.replace('components/', '')}.jsx`
    ];

    for (const path of possiblePaths) {
      const moduleLoader = featureModules[path] || componentModules[path];
      if (moduleLoader) {
        return lazy(moduleLoader as () => Promise<{ default: React.ComponentType }>);
      }
    }

    throw new Error(`Component not found: ${componentPath}`);
  } catch (error) {
    console.warn(`Component not found: ${componentPath}`, error);
    return () => createElement('div', {}, `Component "${componentPath}" not found`);
  }
};

const processRoutes = (routes: any[], userStatus?: string | string[], batch?: string, partner?: string): RouteItem[] => {
  return routes
    .filter((route: any) => route.enabled !== false)
    .filter((route: any) => {
      const routeUserStatus = route.userStatus;

      if (!routeUserStatus || !Array.isArray(routeUserStatus) || routeUserStatus.length === 0) {
        return true;
      }

      const validRouteStatuses = routeUserStatus.filter(status => status && typeof status === 'string' && status.trim() !== '');

      if (validRouteStatuses.length === 0) {
        return true;
      }

      if (!userStatus) {
        return false;
      }

      if (Array.isArray(userStatus)) {
        const validUserStatuses = userStatus.filter(status => status && typeof status === 'string' && status.trim() !== '');

        if (validUserStatuses.length === 0) {
          return false;
        }

        return validUserStatuses.some(status => validRouteStatuses.includes(status));
      } else {
        if (!userStatus || typeof userStatus !== 'string' || userStatus.trim() === '') {
          return false;
        }

        return validRouteStatuses.includes(userStatus);
      }
    })
    .filter((route: any) => !route.batch || route.batch.length === 0 || route.batch.includes(batch))
    .filter((route: any) => !route.partner || route.partner.length === 0 || route.partner.includes(partner))
    .sort((a: any, b: any) => {
      if (typeof userStatus === 'string') {
        const primaryRoute = REDIRECT_PATHS[userStatus as keyof typeof REDIRECT_PATHS];
        if (a.path === primaryRoute) return -1;
        if (b.path === primaryRoute) return 1;
      }
      return 0;
    })
    .map((route: any) => {
      const routeItem: RouteItem = {
        title: route.title,
        path: route.path,
        iconName: route.iconName,
        component: route.component ? getComponent(route.component) : undefined,
        enabled: route.enabled,
        folder: route.folder,
        hideFromMenu: Boolean(route.hideFromMenu),
        userStatus: route.userStatus,
        devOnly: route.devOnly,
        isExternal: route.isExternal
      };

      if (route.children?.length > 0) {
        routeItem.children = processRoutes(route.children, userStatus, batch, partner);
      }

      if (route.subitems?.length > 0) {
        routeItem.subitems = processRoutes(route.subitems, userStatus, batch, partner);
      }

      return routeItem;
    });
};

const getRoutesFromConfig = (config: any, role: string, userStatus?: string | string[], batch?: string, partner?: string): RouteItem[] => {
  const roleRoutes = config?.navigation?.roles?.[role] || config?.roles?.[role];

  if (!roleRoutes) {
    return [];
  }

  const routes = processRoutes(roleRoutes, userStatus, batch, partner);

  const devRoutes = config?.navigation?.devRoutes || config?.devRoutes;
  if (devRoutes && import.meta.env.VITE_ENV === 'development') {
    const processedDevRoutes = processRoutes(devRoutes, userStatus);
    routes.push(...processedDevRoutes);
  }

  return routes;
};

const filterDevOnlyRoutes = (routes: RouteItem[]): RouteItem[] => {
  const isDevelopment = import.meta.env.VITE_ENV === 'development';

  return routes.filter(route => {
    if (route.devOnly && !isDevelopment) return false;

    if (route.subitems?.length) {
      route.subitems = filterDevOnlyRoutes(route.subitems);
    }

    if (route.children?.length) {
      route.children = filterDevOnlyRoutes(route.children);
    }

    return true;
  });
};

const extractAllPaths = (routes: RouteItem[]): RouteItem[] => {
  const paths: RouteItem[] = [];

  const extract = (routeList: RouteItem[]) => {
    routeList.forEach(route => {
      if (route.path && route.component) {
        paths.push(route);
      }
      if (route.subitems?.length) extract(route.subitems);
      if (route.children?.length) extract(route.children);
    });
  };

  extract(routes);
  return paths;
};

const filterNavigationRoutes = (routes: RouteItem[]): RouteItem[] => {
  return routes
    .filter(route => !route.hideFromMenu)
    .map(route => ({
      ...route,
      children: route.children?.length ? filterNavigationRoutes(route.children) : undefined,
      subitems: route.subitems?.length ? filterNavigationRoutes(route.subitems) : undefined
    }));
};

export const getRoutesForRole = (role: string, config?: any, userStatus?: string | string[], batch?: string, partner?: string): RouteItem[] => {
  if (!config) return [];

  const routes = getRoutesFromConfig(config, role, userStatus, batch, partner);
  return filterDevOnlyRoutes(routes);
};

export const getNavigationRoutes = (routes: RouteItem[]): RouteItem[] => {
  return filterNavigationRoutes(routes);
};

export const extractAllRoutePaths = (routes: RouteItem[]): RouteItem[] => {
  return extractAllPaths(routes);
};
